import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ITreeOption } from '@/types';
import { FolderCountersService, IFolderCountItem, IFolderCountsResponse } from '@services/FolderCountersService';
import { folderCountersHub, FolderCountersInvalidatedPayload } from '@/signalr/hubs/FolderCountersHub';
import { ConfigService } from '@/configuration/services/configService';
import { hasFolderCounter, parseSubMenuFromUrl } from '@/components/molecules/Folders/Folders.utils';

const DEFAULT_DEBOUNCE_MS = 500;
const DEFAULT_VISIBILITY_REFRESH_COOLDOWN_MS = 30_000;
/** Разброс full-fetch после переподключения: после рестарта сервера клиенты не приходят пересчитывать разом. */
const RECONNECT_REFETCH_JITTER_MS = 5_000;

const EMPTY_RESPONSE: IFolderCountsResponse = { counts: {} };

const isTabHidden = (): boolean => typeof document !== 'undefined' && document.visibilityState === 'hidden';

function getTimings(): { debounceMs: number; visibilityRefreshCooldownMs: number } {
    const cfg = ConfigService.get?.()?.application?.counters;
    return {
        debounceMs: cfg?.debounceMs ?? DEFAULT_DEBOUNCE_MS,
        visibilityRefreshCooldownMs: cfg?.visibilityRefreshCooldownMs ?? DEFAULT_VISIBILITY_REFRESH_COOLDOWN_MS,
    };
}

export interface UseFolderCountsResult {
    counts: Map<string, number>;
    loading: Set<string>;
    refresh(): void;
}

interface CollectedItems {
    tasks: IFolderCountItem[];
    documents: IFolderCountItem[];
    /** countKey → flowKeys этого counter'а. undefined = catch-all. */
    flowKeys: Map<string, number[] | undefined>;
}

interface FetchSubset {
    tasks: IFolderCountItem[];
    documents: IFolderCountItem[];
}

/** Items, которые надо запросить ещё раз не раньше чем через retryAfterMs (0 — когда вкладка станет видимой). */
interface FetchRetry {
    subset: FetchSubset;
    retryAfterMs: number;
}

interface RetryState {
    timer: ReturnType<typeof setTimeout> | null;
    /** Items, ждущие повтора; копятся, пока таймер не сработал или вкладка скрыта. */
    subset: FetchSubset | null;
    ctrl: AbortController | null;
}

/**
 * Хук lazy-счётчиков меню Задачи/Документы.
 *
 *  - `doFullFetch` — все counter-ы дерева: mount, возврат во вкладку после cooldown, переподключение хаба.
 *  - `doSubsetFetch` — counter-ы, затронутые событием CountersInvalidated.
 * Каждый запрос добавляет свои counter-ы в подписки пользователя на сервере. Значения, которые сервер не
 * пересчитал (retryAfterMs), запрашиваются повторно одним отложенным запросом на экземпляр хука.
 */
export function useFolderCounts(list: ITreeOption[]): UseFolderCountsResult {
    const [counts, setCounts] = useState<Map<string, number>>(new Map());
    const [loading, setLoading] = useState<Set<string>>(new Set());

    const timings = useMemo(getTimings, []);

    const collected = useMemo(() => collect(list), [list]);

    const collectedKey = useMemo(
        () =>
            JSON.stringify([
                collected.tasks.map((x) => `${x.submenu}/${x.name}`),
                collected.documents.map((x) => `${x.submenu}/${x.name}`),
            ]),
        [collected],
    );

    // Без счётчиков в дереве хаб не нужен — не держим лишнее SignalR-соединение.
    const hasCounters = hasItems(collected);

    const latestCollected = useRef(collected);
    latestCollected.current = collected;

    const mounted = useRef(true);
    const lastFetchTs = useRef<number>(0);
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingPayloads = useRef<FolderCountersInvalidatedPayload[]>([]);
    /** Full-fetch пропущен в скрытой вкладке — при возврате делаем его без оглядки на cooldown. */
    const deferredFullFetch = useRef(false);
    const retry = useRef<RetryState>({ timer: null, subset: null, ctrl: null });
    /** Порядковый номер запроса, чей ответ применён к ключу: ответ более раннего запроса не затирает свежий. */
    const requestSeq = useRef(0);
    const appliedSeq = useRef(new Map<string, number>());
    const scheduleRetryRef = useRef<(next: FetchRetry) => void>(() => {});

    /**
     * Общий путь HTTP-fetch'а: merge ответа в counts/loading. `cleanupMissingKeys` — только для full-fetch
     * без повтора: subset не покрывает всё дерево, а пропущенные сервером ключи остаются как были.
     * В скрытой вкладке запрос не уходит — items возвращаются на повтор после возврата.
     */
    const executeFetch = useCallback(
        async (
            t: IFolderCountItem[],
            d: IFolderCountItem[],
            cleanupMissingKeys: boolean,
            signal?: AbortSignal,
        ): Promise<FetchRetry | null> => {
            if (isTabHidden()) return { subset: { tasks: t, documents: d }, retryAfterMs: 0 };

            const keys = [...t, ...d].map(responseKey);
            const seq = ++requestSeq.current;
            setLoading((prev) => {
                if (keys.every((key) => prev.has(key))) return prev;
                const next = new Set(prev);
                keys.forEach((key) => next.add(key));
                return next;
            });

            try {
                const [tasksRes, docsRes] = await Promise.all([
                    t.length > 0 ? FolderCountersService.fetchTasks(t, signal) : EMPTY_RESPONSE,
                    d.length > 0 ? FolderCountersService.fetchDocuments(d, signal) : EMPTY_RESPONSE,
                ]);
                if (signal?.aborted || !mounted.current) return null;

                const tasksRetryMs = tasksRes.retryAfterMs ?? 0;
                const docsRetryMs = docsRes.retryAfterMs ?? 0;
                const retryAfterMs = Math.max(tasksRetryMs, docsRetryMs);

                setCounts((prev) => {
                    let next = prev;
                    const set = (key: string, value: number) => {
                        if ((appliedSeq.current.get(key) ?? 0) > seq) return;
                        appliedSeq.current.set(key, seq);
                        if (next.get(key) === value) return;
                        if (next === prev) next = new Map(prev);
                        next.set(key, value);
                    };
                    Object.entries(tasksRes.counts ?? {}).forEach(([k, v]) => set(k, v));
                    Object.entries(docsRes.counts ?? {}).forEach(([k, v]) => set(k, v));
                    if (cleanupMissingKeys && retryAfterMs <= 0) {
                        const allKeys = new Set([
                            ...latestCollected.current.tasks.map(responseKey),
                            ...latestCollected.current.documents.map(responseKey),
                        ]);
                        for (const k of next.keys()) {
                            if (allKeys.has(k)) continue;
                            if (next === prev) next = new Map(prev);
                            next.delete(k);
                        }
                    }
                    return next;
                });
                lastFetchTs.current = Date.now();
                if (retryAfterMs <= 0) return null;
                return {
                    subset: { tasks: tasksRetryMs > 0 ? t : [], documents: docsRetryMs > 0 ? d : [] },
                    retryAfterMs,
                };
            } catch (err) {
                if (!signal?.aborted) {
                    console.warn('useFolderCounts: batch fetch failed', err);
                }
                return null;
            } finally {
                if (mounted.current) {
                    setLoading((prev) => {
                        if (!keys.some((key) => prev.has(key))) return prev;
                        const next = new Set(prev);
                        keys.forEach((key) => next.delete(key));
                        return next;
                    });
                }
            }
        },
        [],
    );

    const cancelRetry = useCallback(() => {
        const state = retry.current;
        if (state.timer != null) clearTimeout(state.timer);
        state.ctrl?.abort();
        retry.current = { timer: null, subset: null, ctrl: null };
    }, []);

    /** Взвести таймер повтора накопленных items, если он не взведён. */
    const armRetry = useCallback(
        (delayMs: number) => {
            const state = retry.current;
            if (state.timer != null || !state.subset || !mounted.current) return;
            state.timer = setTimeout(
                () => {
                    state.timer = null;
                    if (isTabHidden() || !state.subset) return;
                    const pending = state.subset;
                    state.subset = null;
                    state.ctrl?.abort();
                    const ctrl = new AbortController();
                    state.ctrl = ctrl;
                    void executeFetch(pending.tasks, pending.documents, false, ctrl.signal).then((next) => {
                        if (next && !ctrl.signal.aborted) scheduleRetryRef.current(next);
                    });
                },
                Math.max(delayMs, timings.debounceMs),
            );
        },
        [executeFetch, timings.debounceMs],
    );

    /** Один отложенный повтор на экземпляр хука: повторные retryAfterMs, пока таймер взведён, только добавляют items. */
    const scheduleRetry = useCallback(
        (next: FetchRetry) => {
            if (!mounted.current) return;
            retry.current.subset = mergeSubsets(retry.current.subset, next.subset);
            armRetry(next.retryAfterMs);
        },
        [armRetry],
    );
    scheduleRetryRef.current = scheduleRetry;

    const doFullFetch = useCallback(
        async (signal?: AbortSignal) => {
            // Full-fetch покрывает все items — отложенный повтор не нужен.
            cancelRetry();
            const { tasks, documents } = latestCollected.current;
            if (tasks.length === 0 && documents.length === 0) {
                deferredFullFetch.current = false;
                setCounts((prev) => (prev.size === 0 ? prev : new Map()));
                setLoading((prev) => (prev.size === 0 ? prev : new Set()));
                return;
            }
            if (isTabHidden()) {
                deferredFullFetch.current = true;
                return;
            }
            deferredFullFetch.current = false;
            const next = await executeFetch(tasks, documents, true, signal);
            if (next && !signal?.aborted) scheduleRetry(next);
        },
        [cancelRetry, executeFetch, scheduleRetry],
    );

    const doSubsetFetch = useCallback(
        async (subset: FetchSubset, signal?: AbortSignal) => {
            const next = await executeFetch(subset.tasks, subset.documents, false, signal);
            if (next && !signal?.aborted) scheduleRetry(next);
        },
        [executeFetch, scheduleRetry],
    );

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            cancelRetry();
        };
    }, [cancelRetry]);

    // Первичный и реактивный fetch на изменение состава counter-ов в дереве.
    useEffect(() => {
        const ctrl = new AbortController();
        void doFullFetch(ctrl.signal);
        return () => ctrl.abort();
    }, [collectedKey, doFullFetch]);

    // SignalR push-инвалидация: события копятся в pendingPayloads, debounce склеивает
    // пачку NOTIFY (bulk-операция) в один subset-refetch. selectAffected фильтрует
    // counter-ы по flowKey scope, чтобы не рефетчить лишнее.
    useEffect(() => {
        if (!hasCounters) return;
        // Идемпотентно для двух экземпляров хука; окончательный отказ старта хук не ломает —
        // счётчики живут на HTTP, повторный старт — при возврате во вкладку.
        folderCountersHub.ensureStarted();

        let subsetCtrl: AbortController | null = null;

        const unsub = folderCountersHub.onCountersInvalidated((payload) => {
            FolderCountersService.noteInvalidated();
            pendingPayloads.current.push(payload);
            if (debounceTimer.current != null) {
                clearTimeout(debounceTimer.current);
            }
            debounceTimer.current = setTimeout(() => {
                debounceTimer.current = null;
                const payloads = pendingPayloads.current;
                pendingPayloads.current = [];
                const subset = selectAffected(latestCollected.current, payloads);
                if (!hasItems(subset)) return;
                subsetCtrl?.abort();
                subsetCtrl = new AbortController();
                void doSubsetFetch(subset, subsetCtrl.signal);
            }, timings.debounceMs);
        });

        // События за время обрыва потеряны — перечитываем все счётчики.
        let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
        const unsubReconnected = folderCountersHub.onReconnected(() => {
            if (reconnectTimer != null) return;
            reconnectTimer = setTimeout(() => {
                reconnectTimer = null;
                void doFullFetch();
            }, Math.random() * RECONNECT_REFETCH_JITTER_MS);
        });

        return () => {
            unsub();
            unsubReconnected();
            if (reconnectTimer != null) clearTimeout(reconnectTimer);
            pendingPayloads.current = [];
            if (debounceTimer.current != null) {
                clearTimeout(debounceTimer.current);
                debounceTimer.current = null;
            }
            subsetCtrl?.abort();
            subsetCtrl = null;
        };
    }, [hasCounters, doFullFetch, doSubsetFetch, timings.debounceMs]);

    // Возврат во вкладку → полный refresh, но не чаще visibilityRefreshCooldownMs (кроме отложенного
    // в скрытой вкладке); иначе — повтор items, отложенных в скрытой вкладке. Заодно мягко поднимаем хаб.
    useEffect(() => {
        const onVis = () => {
            if (typeof document === 'undefined') return;
            if (document.visibilityState !== 'visible') return;
            if (hasItems(latestCollected.current)) folderCountersHub.ensureStarted();
            if (!deferredFullFetch.current && Date.now() - lastFetchTs.current < timings.visibilityRefreshCooldownMs) {
                armRetry(0);
                return;
            }
            void doFullFetch();
        };
        const onOnline = () => {
            if (hasItems(latestCollected.current)) folderCountersHub.ensureStarted();
        };
        document.addEventListener('visibilitychange', onVis);
        window.addEventListener('online', onOnline);
        return () => {
            document.removeEventListener('visibilitychange', onVis);
            window.removeEventListener('online', onOnline);
        };
    }, [armRetry, doFullFetch, timings.visibilityRefreshCooldownMs]);

    const refresh = useCallback(() => {
        if (debounceTimer.current != null) {
            clearTimeout(debounceTimer.current);
            debounceTimer.current = null;
        }
        void doFullFetch();
    }, [doFullFetch]);

    return { counts, loading, refresh };
}

function collect(list: ITreeOption[]): CollectedItems {
    const tasks: IFolderCountItem[] = [];
    const docs: IFolderCountItem[] = [];
    const flowKeys = new Map<string, number[] | undefined>();
    walk(list);
    return { tasks, documents: docs, flowKeys };

    function walk(items?: ITreeOption[]) {
        items?.forEach((item) => {
            const parsed = parseItem(item);
            if (parsed) {
                const kind = detectKind(item.url);
                if (kind === 'tasks') tasks.push(parsed);
                else if (kind === 'documents') docs.push(parsed);
                flowKeys.set(
                    responseKey(parsed),
                    item.counterFlowKeys && item.counterFlowKeys.length > 0 ? item.counterFlowKeys : undefined,
                );
            }
            walk(item.children);
        });
    }
}

function parseItem(item: ITreeOption): IFolderCountItem | null {
    if (!hasFolderCounter(item) || !item.url) return null;
    const submenu = parseSubMenuFromUrl(item.url);
    if (!submenu) return null;
    return { submenu, name: item.countKey };
}

/**
 * /tasks?... → tasks; /documents?... → documents (в т.ч. subMenu=bk идёт как
 * /documents?subMenu=bk — это тоже Documents-тип, обрабатывается серверным
 * IDocumentsSearchFolderService).
 *
 * Важно: матч идёт по префиксу URL, а не по subMenu. Если когда-то появится
 * самостоятельный URL вида /bk?... (без /documents-обёртки) — здесь нужно будет
 * добавить третью ветку, иначе counter молча отвалится.
 */
function detectKind(url?: string): 'tasks' | 'documents' | null {
    if (!url) return null;
    if (url.startsWith('/tasks')) return 'tasks';
    if (url.startsWith('/documents')) return 'documents';
    return null;
}

function responseKey(item: IFolderCountItem): string {
    return item.name;
}

function hasItems(subset: FetchSubset): boolean {
    return subset.tasks.length > 0 || subset.documents.length > 0;
}

function mergeSubsets(a: FetchSubset | null, b: FetchSubset): FetchSubset {
    if (!a) return { tasks: [...b.tasks], documents: [...b.documents] };
    const merge = (x: IFolderCountItem[], y: IFolderCountItem[]) => {
        const keys = new Set(x.map(responseKey));
        return [...x, ...y.filter((item) => !keys.has(responseKey(item)))];
    };
    return { tasks: merge(a.tasks, b.tasks), documents: merge(a.documents, b.documents) };
}

/**
 * Выбирает subset items для refetch при batch'е CountersInvalidated.
 * Counter попадает в subset, если хотя бы одно событие его kind matches:
 *   - его scope пуст (catch-all) → реагирует на любое событие kind;
 *   - событие без flowKey/oldFlowKey → консервативно matches всех;
 *   - flowKey события совпадает с flowKey scope counter'а.
 */
function selectAffected(collected: CollectedItems, payloads: FolderCountersInvalidatedPayload[]): FetchSubset {
    return {
        tasks: pickMatching(collected.tasks, payloads, 'tasks', collected.flowKeys),
        documents: pickMatching(collected.documents, payloads, 'documents', collected.flowKeys),
    };
}

function pickMatching(
    items: IFolderCountItem[],
    payloads: FolderCountersInvalidatedPayload[],
    kind: 'tasks' | 'documents',
    flowKeysMap: Map<string, number[] | undefined>,
): IFolderCountItem[] {
    if (items.length === 0) return [];

    const eventFlowKeys = new Set<number>();
    let hasUnknownScopeEvent = false;
    let hasAnyEventOfKind = false;
    for (const e of payloads) {
        if (e.kind !== kind) continue;
        hasAnyEventOfKind = true;
        if (e.flowKey == null && e.oldFlowKey == null) {
            hasUnknownScopeEvent = true;
            break;
        }
        if (typeof e.flowKey === 'number') eventFlowKeys.add(e.flowKey);
        if (typeof e.oldFlowKey === 'number') eventFlowKeys.add(e.oldFlowKey);
    }

    if (!hasAnyEventOfKind) return [];
    if (hasUnknownScopeEvent) return items;

    return items.filter((item) => {
        const flows = flowKeysMap.get(responseKey(item));
        if (!flows?.length) return true; // catch-all
        return flows.some((f) => eventFlowKeys.has(f));
    });
}
